import { Td } from 'components/Td/Td'
import { CircleButton, ScheduleMonthlyCard, ScheduleOption, ScheduleOptionsWrapper } from 'pages/Shifts/Shifts.styled'
import { Flex, ProfilePicture, Table, TableBody, TableWrapper, Th, Tr } from 'styling/GlobalStyles'
import { addZero } from 'utils/helpers'
import { BodyText, OtherText } from 'styling/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Avatar from '../../assets/images/profileAvatar.png'
import { AppDispatch, RootState } from 'store'
import { setConfirmModal } from 'features/global/globalSlice'
import { addWorkSchedule, addWorkScheduleArray, clearWorkSchedule, getWorkSchedules, removeWorkSchedule } from 'features/work-schedules/workScheduleSlice'
import { ISingleShift } from 'types/shifts.interface'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { clearShiftsList, getShifts } from 'features/shifts/shiftsSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { IGetAllWorkSchedulesSingleResponse } from 'types/api/workScheduleApi.interface'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { SunriseIcon } from './Icons/SunriseIcon'
import { SunsetIcon } from './Icons/SunsetIcon'
import { RotationIcon } from './Icons/RotationIcon'
import { BeachIcon } from './Icons/BeachIcon'
import { DrinkIcon } from './Icons/DrinkIcon'
import { HeartIcon } from './Icons/HeartIcon'
import { MinusIcon } from './Icons/MinusIcon'
import { SunIcon } from './Icons/SunIcon'
import styled from 'styled-components'
import { FloatingDropdown } from 'components/FloatingDropdown/FloatingDropdown'

const getMonthlyGridTemplate = (daysCount: number) => `2.75rem minmax(13rem, 15rem) repeat(${daysCount}, minmax(0, 1fr))`

const MonthlyHeaderRow = styled(Th) <{ daysCount: number }>`
    display: grid;
    grid-template-columns: ${({ daysCount }) => getMonthlyGridTemplate(daysCount)};
    justify-content: unset;
    gap: 0;
    width: 100%;
    min-width: 0;
`

const MonthlyBodyRow = styled(Tr) <{ daysCount: number }>`
    display: grid;
    grid-template-columns: ${({ daysCount }) => getMonthlyGridTemplate(daysCount)};
    justify-content: unset;
    gap: 0;
    width: 100%;
    min-width: 0;
`

export const ScheduleMonthlyDisplay = ({ params, workSchedules }: { params: any, workSchedules: IGetAllWorkSchedulesSingleResponse[] }) => {
    const [checked, setChecked] = useState([])
    const [openDropdown, setOpenDropdown] = useState<{ userId: number, day: number, anchor: HTMLElement } | null>(null)
    const dispatch = useDispatch<AppDispatch>()
    const { shifts } = useSelector((store: RootState) => store.shifts)
    const [isOutHouse, setIsOutHouse] = useState(false)

    useQueryPagination({ fetchFunctions: [getWorkSchedules], cleanUpFunctions: [clearWorkSchedule], params, otherFunctions: [() => setChecked([])], dontFetchIfEmpty: ['dateTo', 'dateFrom'] })

    useEffect(() => {
        dispatch(getShifts({ page: 1, limit: 99 }))
        return () => {
            dispatch(clearShiftsList())
        }
    }, [])

    const getIcon = (iconName: string, color: string) => {
        switch (iconName) {
            case 'sunrise':
                return <SunriseIcon size={16} stroke={color || '#ff9900'} />
            case 'sunset':
                return <SunsetIcon size={16} stroke={color || '#ff9900'} />
            case 'rotation':
                return <RotationIcon size={16} stroke={color || '#ff9900'} />
            case 'beach':
                return <BeachIcon size={16} stroke={color || '#ff9900'} />
            case 'drink':
                return <DrinkIcon size={16} stroke={color || '#ff9900'} />
            case 'heart':
                return <HeartIcon size={16} stroke={color || '#ff9900'} />
            case 'sun':
                return <SunIcon size={16} stroke={color || '#ff9900'} />
            default:
                return <SunriseIcon size={16} stroke={color || '#ff9900'} />
        }
    }

    const monthlyHeaderDays = useMemo(() => {
        const arr: { dayNumber: string, dayOfWeek: number }[] = []
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = new Date(year, month, 1)
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        for (let i = 1; i <= daysInMonth; i++) {
            arr.push({
                dayNumber: `${addZero(day.getDate())}`,
                dayOfWeek: day.getDay(),
            })
            day.setDate(day.getDate() + 1)
        }

        return arr
    }, [params.dateFrom])

    interface ShiftWithDate {
        date: string;
        shift: ISingleShift;
    }

    const handleOuthouse = (e, v: boolean) => {
        e.stopPropagation()
        setIsOutHouse(v)
    }

    const handleWorkSchedule = (shiftId: number, userId: number, date) => {
        if (!shiftId) {
            dispatch(
                setConfirmModal({
                    isOpen: true,
                    onSave: async () => {
                        await dispatch(removeWorkSchedule({ userId, date }))
                    },
                    word: 'unassign shift',
                }),
            )

            return
        }
        setChecked([])

        checked.length < 2
            ? dispatch(addWorkSchedule({ userId, date, shiftId, isOutHouse }))
            : dispatch(addWorkScheduleArray({ users: checked, date, shiftId, isOutHouse }))
    }

    const renderShiftInputs = (dates: ShiftWithDate[], userId: number) => {
        const arr = []
        const day = new Date(params.dateFrom)
        const date = new Date(params.dateFrom)
        const year = date.getFullYear()
        const month = date.getMonth()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        // Precompute a map for O(1) lookups instead of Array.find per day
        const dateToShiftMap = new Map<number, ShiftWithDate>()
        for (const d of dates) {
            dateToShiftMap.set(new Date(d.date).setHours(0, 0, 0, 0), d)
        }
        for (let i = 1; i <= daysInMonth; i++) {
            const shiftForTheDay: ShiftWithDate | undefined = dateToShiftMap.get(new Date(day).setHours(0, 0, 0, 0))
            const handleDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
                const anchor = e.currentTarget
                if (openDropdown && openDropdown.userId === userId && openDropdown.day === i) {
                    setOpenDropdown(null)
                } else {
                    setOpenDropdown({ userId, day: i, anchor })
                }
            }
            const color = shiftForTheDay?.shift?.color ? '#' + shiftForTheDay?.shift.color : '#ff9900'

            const getDay = () => {
                const dt = new Date(params.dateFrom)
                dt.setDate(dt.getDate() + i - 1)
                return dt
            }

            arr.push(
                <Td key={`${userId}-${i}`}>
                    <ScheduleMonthlyCard color={shiftForTheDay?.shift ? color : null} onClick={handleDropdown}>
                        {shiftForTheDay?.shift?.name ? getIcon(shiftForTheDay?.shift?.icon, shiftForTheDay?.shift?.iconColor ? '#' + shiftForTheDay?.shift?.iconColor : '#fff') : <MinusIcon stroke='#fff' size={16} />}
                        <FloatingDropdown
                            open={!!(openDropdown && openDropdown.userId === userId && openDropdown.day === i)}
                            onOpenChange={(o) => { if (!o) setOpenDropdown(null) }}
                            reference={openDropdown && openDropdown.userId === userId && openDropdown.day === i ? openDropdown.anchor : null}
                        >
                            <ScheduleOptionsWrapper>
                                {shiftForTheDay?.shift && <ScheduleOption onClick={() => handleWorkSchedule(null, userId, getDay())} >
                                    <OtherText color='red' >Remove Shift</OtherText>
                                </ScheduleOption>}
                                {shifts.map(s => <ScheduleOption onClick={() => handleWorkSchedule(s.id, userId, getDay())} key={s.id}>
                                    <OtherText>{s.name}</OtherText>
                                </ScheduleOption>)}
                                <Flex width='100%' column padding='0 1rem' >
                                    <ScheduleOption onClick={(e) => handleOuthouse(e, false)} style={{ borderTop: '1px solid white' }} >
                                        <CheckBox state={!isOutHouse} title='Inhouse' />
                                    </ScheduleOption>
                                    <ScheduleOption onClick={(e) => handleOuthouse(e, true)} >
                                        <CheckBox state={isOutHouse} title='Outhouse' />
                                    </ScheduleOption>
                                </Flex>
                            </ScheduleOptionsWrapper>
                        </FloatingDropdown>
                    </ScheduleMonthlyCard>
                </Td>,
            )
            day.setDate(day.getDate() + 1)
        }

        return arr
    }

    const toggle = (id: number) => {
        if (checked.includes(id)) {
            setChecked((prev) => {
                return prev.filter((item) => item !== id)
            })
        } else {
            setChecked((prev) => (!prev ? [id] : [...prev, id]))
        }
    }

    const toggleAll = () => {
        if (checked.length < workSchedules.length) {
            const allIds = workSchedules.map((a) => a.id)
            setChecked(allIds)
        } else {
            setChecked([])
        }
    }

    return <TableWrapper style={{ height: '100%' }}>
        <Table style={{ height: '100%' }}>
            <MonthlyHeaderRow daysCount={monthlyHeaderDays.length}>
                <Td noTooltip>
                    <Flex justcenter width='100%'>
                        <CircleButton
                            onClick={toggleAll}
                            checked={checked.length === workSchedules?.length}
                        />
                    </Flex>
                </Td>
                <Td noTooltip>
                    <BodyText weight='bold'>Employee</BodyText>
                </Td>
                {monthlyHeaderDays.map(({ dayNumber, dayOfWeek }, index) => (
                    <Td noTooltip key={`schedule-month-day-${index + 1}`}>
                        <Flex justcenter width='100%'>
                            <OtherText color={dayOfWeek == 0 || dayOfWeek == 6 ? '#ff9900' : null}>{dayNumber}</OtherText>
                        </Flex>
                    </Td>
                ))}
            </MonthlyHeaderRow>
            <TableBody>
                {workSchedules?.length ?
                    workSchedules.map((item: IGetAllWorkSchedulesSingleResponse) => {
                        const id = item.id
                        return (
                            <MonthlyBodyRow small borderHover key={id} daysCount={monthlyHeaderDays.length}>
                                <Td noTooltip>
                                    <Flex justcenter width='100%'>
                                        <CircleButton
                                            orange
                                            checked={checked.includes(id)}
                                            onClick={() => toggle(id)}
                                        />
                                    </Flex>
                                </Td>
                                <Td noTooltip>
                                    <Flex center gap='1rem'>
                                        <ProfilePicture >
                                            <img
                                                src={item.profileImageUrl || Avatar}
                                                alt=''
                                                onError={({ currentTarget }) => {
                                                    currentTarget.onerror = null
                                                    currentTarget.src = Avatar
                                                }}
                                            />
                                        </ProfilePicture>
                                        <BodyText>{item.firstName + ' ' + item.lastName}</BodyText>
                                    </Flex>
                                </Td>
                                {renderShiftInputs(item.schedules, item.id)}
                            </MonthlyBodyRow>
                        )
                    }) : <NoItems title='Employees' />}
            </TableBody>
        </Table>
    </TableWrapper>
}
